/**
 * PM module query and mutation helpers using Gen 2 typed client.
 */
import { authClient } from "../utils/amplifyClient";

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

export async function fetchPMConfigurationsByCompany(companyId: string) {
    const { data } = await authClient.models.PMConfiguration.listPMConfigurationByCompanyId({ companyId });
    return data;
}

export async function fetchPMConfigurationsByUnit(unitId: string) {
    const { data } = await authClient.models.PMConfiguration.listPMConfigurationByUnitId({ unitId });
    return data;
}

export async function fetchPMConfiguration(id: string) {
    const { data } = await authClient.models.PMConfiguration.get({ id });
    return data;
}

export async function fetchPMReportsByCompany(
    companyId: string,
    createdAt?: { between?: [string, string] },
) {
    const { data } = await authClient.models.PMReport.listPMReportByCompanyIdAndCreatedAt({
        companyId,
        ...(createdAt?.between ? { sortDirection: 'DESC' } : {}),
    });
    // Filter by date range client-side if needed
    if (createdAt?.between) {
        return data.filter(r => r.createdAt && r.createdAt >= createdAt.between![0] && r.createdAt <= createdAt.between![1]);
    }
    return data;
}

// ---------------------------------------------------------------------------
// Mutation helpers — PMConfiguration
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createPMConfig(input: any) {
    const { data } = await authClient.models.PMConfiguration.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updatePMConfig(input: any) {
    const { data } = await authClient.models.PMConfiguration.update(input);
    return data;
}

export async function deletePMConfig(id: string) {
    const { data } = await authClient.models.PMConfiguration.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Mutation helpers — PMServiceLevel
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createServiceLevel(input: any) {
    const { data } = await authClient.models.PMServiceLevel.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateServiceLevel(input: any) {
    const { data } = await authClient.models.PMServiceLevel.update(input);
    return data;
}

export async function deleteServiceLevel(id: string) {
    const { data } = await authClient.models.PMServiceLevel.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Mutation helpers — PMPartsAndProcedure
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createPartsAndProcedure(input: any) {
    const { data } = await authClient.models.PMPartsAndProcedure.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updatePartsAndProcedure(input: any) {
    const { data } = await authClient.models.PMPartsAndProcedure.update(input);
    return data;
}

export async function deletePartsAndProcedure(id: string) {
    const { data } = await authClient.models.PMPartsAndProcedure.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Mutation helpers — PMNotificationSetting
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createNotificationSetting(input: any) {
    const { data } = await authClient.models.PMNotificationSetting.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateNotificationSetting(input: any) {
    const { data } = await authClient.models.PMNotificationSetting.update(input);
    return data;
}

export async function deleteNotificationSetting(id: string) {
    const { data } = await authClient.models.PMNotificationSetting.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Mutation helpers — PMReport
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createPMReport(input: any) {
    const { data, errors } = await authClient.models.PMReport.create(input);
    if (errors?.length || !data?.id) {
        const message = errors?.map((error: { message?: string }) => error.message).filter(Boolean).join('; ');
        throw new Error(message || 'Failed to save PM service');
    }
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updatePMReport(input: any) {
    const { data } = await authClient.models.PMReport.update(input);
    return data;
}

export async function deletePMReport(id: string) {
    const { data } = await authClient.models.PMReport.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Composite helper — Enroll unit in PM
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function enrollUnitInPM(input: any) {
    const { data } = await authClient.models.PMConfiguration.create(input);
    return data;
}

// ---------------------------------------------------------------------------
// Labor helpers
// ---------------------------------------------------------------------------

export async function fetchLaborCategoriesByCompany(companyId: string) {
    const { data } = await authClient.models.LaborCategory.listLaborCategoryByCompanyId({ companyId });
    return data;
}

export async function fetchLaborRatesByCategory(categoryId: string) {
    const { data } = await authClient.models.LaborRate.listLaborRateByCategoryIdAndEffectiveDate({ categoryId });
    return data;
}

export async function getCurrentLaborRate(categoryId: string) {
    // NOTE: sortDirection/limit were historically passed in the first (index-input)
    // argument, where the typed client ignores them at runtime. Preserved as-is to
    // avoid a runtime behavior change; see wave report. Spread keeps the original
    // (no-op) payload while satisfying excess-property checking, matching the
    // fetchPMReportsByCompany pattern above.
    const { data } = await authClient.models.LaborRate.listLaborRateByCategoryIdAndEffectiveDate({
        categoryId,
        ...{ sortDirection: 'DESC' as const, limit: 1 },
    });
    return data[0] ?? null;
}

// ---------------------------------------------------------------------------
// PM Templates (using raw GraphQL — model not yet in Gen2 schema)
// ---------------------------------------------------------------------------



function throwIfTemplateErrors(errors: { message: string }[] | undefined, fallback: string) {
    if (errors && errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join('; ') || fallback);
    }
}

function templateMatrixData(value: unknown) {
    if (typeof value !== 'string') return value;
    return JSON.parse(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchPMTemplatesByCompany(companyId: string): Promise<any[]> {
    const { data, errors } = await authClient.models.PMTemplate.listPMTemplateByCompanyId({ companyId });
    throwIfTemplateErrors(errors, 'Failed to load templates');
    return data ?? [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchPMTemplatesByCategory(categoryId: string): Promise<any[]> {
    const { data, errors } = await authClient.models.PMTemplate.listPMTemplateByCategoryId({ categoryId });
    throwIfTemplateErrors(errors, 'Failed to load templates');
    return data ?? [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createPMTemplate(input: any): Promise<any> {
    const { data, errors } = await authClient.models.PMTemplate.create({
        ...input,
        matrixData: templateMatrixData(input.matrixData),
    });
    throwIfTemplateErrors(errors, 'Failed to save template');
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updatePMTemplate(input: any): Promise<any> {
    const { data, errors } = await authClient.models.PMTemplate.update({
        ...input,
        ...(input.matrixData !== undefined ? { matrixData: templateMatrixData(input.matrixData) } : {}),
    });
    throwIfTemplateErrors(errors, 'Failed to update template');
    return data;
}

export async function deletePMTemplate(id: string) {
    const { data, errors } = await authClient.models.PMTemplate.delete({ id });
    throwIfTemplateErrors(errors, 'Failed to delete template');
    return data;
}

export async function fetchPMMatrixSnapshots(pmConfigurationId: string) {
    const { data, errors } = await authClient.models.PMMatrixSnapshot.listPMMatrixSnapshotByPmConfigurationId({
        pmConfigurationId,
    });
    throwIfTemplateErrors(errors, 'Failed to load the saved matrix copy');
    return data ?? [];
}

export async function createPMMatrixSnapshot(input: {
    companyId: string;
    pmConfigurationId: string;
    unitId: string;
    matrixData: unknown;
    createdByUserId?: string;
    createdByUserName?: string;
}) {
    const { data, errors } = await authClient.models.PMMatrixSnapshot.create({
        companyId: input.companyId,
        pmConfigurationId: input.pmConfigurationId,
        unitId: input.unitId,
        matrixData: templateMatrixData(input.matrixData),
        createdByUserId: input.createdByUserId,
        createdByUserName: input.createdByUserName,
    });
    throwIfTemplateErrors(errors, 'Failed to keep a copy of this matrix');
    return data;
}
